// src/pages/admin/Dashboard1.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Accordion, Spinner } from "react-bootstrap";
import { selectToken } from "../../auth/authSelectors";
import dashboardApi from "../../api/admin/dashboard";
import "./Dashboard1.css";

// ---------- helpers ----------
const toDate = (iso) => new Date(`${iso}T00:00:00`);
const fmtFull = (iso) =>
  toDate(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtMonth = (iso) => toDate(iso).toLocaleDateString("en-IN", { month: "short" });
const fmtDayNum = (iso) => toDate(iso).getDate();
const fmtWeekday = (iso) => toDate(iso).toLocaleDateString("en-IN", { weekday: "short" });
const initials = (s) =>
  `${(s.name || "").charAt(0)}${(s.last_name || "").charAt(0)}`.toUpperCase() || "?";
const whenLabel = (d) => (d === 0 ? "Today" : d === 1 ? "Tomorrow" : `In ${d} days`);
const joinedLabel = (d) => (d === 0 ? "Joined today" : d === 1 ? "Joined yesterday" : `Joined ${d} days ago`);
const monthName = (iso, offset = 0) => {
  const d = iso ? toDate(iso) : new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};
const keyOf = (s) => s.admission_number || `${s.full_name}-${s.dob}`;

function Avatar({ student, size = 44, ring = false }) {
  const [broken, setBroken] = useState(false);
  const cls = `bd-avatar ${ring ? "bd-avatar--ring" : ""}`;
  const style = { width: size, height: size, fontSize: size * 0.34 };
  if (student.profile_image && !broken) {
    return (
      <img src={student.profile_image} alt="" className={cls} style={style} onError={() => setBroken(true)} />
    );
  }
  return (
    <span className={`${cls} bd-avatar--fallback`} style={style} aria-hidden="true">
      {initials(student)}
    </span>
  );
}

// ---------- today spotlight card ----------
function SpotlightCard({ student, onCreatePoster }) {
  return (
    <article className="bd-spot">
      <Avatar student={student} size={72} ring />
      <div className="bd-spot__body">
        <h3 className="bd-spot__name" title={student.full_name}>{student.full_name}</h3>
        <p className="bd-spot__dob">
          DOB {fmtFull(student.dob)} · Turns {student.turning_age} today
        </p>
        <p className="bd-spot__id">
          {student.admission_number ? `ID ${student.admission_number}` : "No admission no."}
        </p>
      </div>
      <button type="button" className="bd-btn bd-btn--light" onClick={() => onCreatePoster(student)}>
        Create poster
      </button>
    </article>
  );
}

// ---------- upcoming card ----------
function UpcomingCard({ student, onCreatePoster }) {
  const soon = student.days_until <= 3;
  return (
    <article className="bd-card">
      <div className="bd-date" aria-label={`Birthday on ${fmtWeekday(student.next_birthday)} ${fmtDayNum(student.next_birthday)} ${fmtMonth(student.next_birthday)}`}>
        <strong>{fmtDayNum(student.next_birthday)}</strong>
        <span>{fmtMonth(student.next_birthday)}</span>
      </div>
      <div className="bd-card__main">
        <div className="bd-card__top">
          <Avatar student={student} size={34} />
          <h3 className="bd-card__name" title={student.full_name}>{student.full_name}</h3>
        </div>
        <p className="bd-card__dob">
          <span>DOB</span> {fmtFull(student.dob)}
        </p>
        <div className="bd-card__foot">
          <span className={`bd-chip ${soon ? "bd-chip--soon" : ""}`}>{whenLabel(student.days_until)}</span>
          <span className="bd-card__turn">Turns {student.turning_age}</span>
        </div>
      </div>
      <button type="button" className="bd-btn bd-btn--ghost" onClick={() => onCreatePoster(student)}>
        Poster
      </button>
    </article>
  );
}

// ---------- new joiner card ----------
function JoinerCard({ student }) {
  return (
    <article className="bd-join">
      <Avatar student={student} size={42} />
      <div className="bd-join__main">
        <h3 className="bd-join__name" title={student.full_name}>{student.full_name}</h3>
        <p className="bd-join__meta">
          {student.admission_number ? `ID ${student.admission_number}` : "No admission no."}
        </p>
      </div>
      <div className="bd-join__when">
        <b><span>DOJ</span> {fmtFull(student.doj)}</b>
        <small className={student.days_since <= 3 ? "is-new" : ""}>{joinedLabel(student.days_since)}</small>
      </div>
    </article>
  );
}

// ---------- main ----------
export default function Dashboard1({ onCreatePoster }) {
  const token = useSelector(selectToken);

  const [data, setData] = useState({
    upcoming: [], others: [], counts: {}, window_days: 30,
    joiners: { current_month: [], previous_month: [] }, server_date: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | week
  const [joinTab, setJoinTab] = useState("current"); // current | previous
  const [openKey, setOpenKey] = useState(null);
  const [query, setQuery] = useState("");

  const handlePoster = useCallback(
    (student) => {
      if (onCreatePoster) onCreatePoster(student);
      else console.info("Create poster for", student.admission_number); // wire to poster route
    },
    [onCreatePoster]
  );

  useEffect(() => {
    if (!token) {
      setError("Authentication failed. Please log in again.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardApi.getDashboardData(token);
        if (res.status !== "success") throw new Error("API Error");
        if (!cancelled) {
          setData({
            upcoming: res.upcoming || [],
            others: res.others || [],
            counts: res.counts || {},
            window_days: res.window_days || 30,
            joiners: {
              current_month: res.new_joiners?.current_month || [],
              previous_month: res.new_joiners?.previous_month || [],
            },
            server_date: res.server_date || null,
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Couldn't load student birthdays. Refresh to try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const todayList = useMemo(() => data.upcoming.filter((s) => s.days_until === 0), [data.upcoming]);
  const soonList = useMemo(() => data.upcoming.filter((s) => s.days_until > 0), [data.upcoming]);
  const weekList = useMemo(() => soonList.filter((s) => s.days_until <= 7), [soonList]);
  const shown = filter === "week" ? weekList : soonList;
  const nextUp = soonList[0] || data.others[0];

  const joinCurrent = data.joiners.current_month;
  const joinPrevious = data.joiners.previous_month;
  const joinShown = joinTab === "current" ? joinCurrent : joinPrevious;
  const curMonthLabel = monthName(data.server_date, 0);
  const prevMonthLabel = monthName(data.server_date, -1);

  const filteredOthers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.others;
    return data.others.filter(
      (s) =>
        s.full_name.toLowerCase().includes(q) ||
        String(s.admission_number || "").toLowerCase().includes(q)
    );
  }, [data.others, query]);

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  const { upcoming = 0, others = 0 } = data.counts;

  return (
    <section className="content py-4">
      <div className="container-fluid bd-page">
        {/* ===== Hero ===== */}
        <header className="bd-hero">
          <div className="bd-hero__top">
            <div>
              <h1 className="bd-hero__title">Birthday centre 🎂</h1>
              <p className="bd-hero__sub">{todayLabel}</p>
            </div>
            {!loading && !error && (
              <div className="bd-stats">
                <div className="bd-stat"><strong>{todayList.length}</strong><span>Today</span></div>
                <div className="bd-stat"><strong>{weekList.length}</strong><span>This week</span></div>
                <div className="bd-stat"><strong>{joinCurrent.length}</strong><span>New this month</span></div>
                <div className="bd-stat"><strong>{upcoming + others}</strong><span>Active students</span></div>
              </div>
            )}
          </div>

          {loading && (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" variant="light" role="status">
                <span className="visually-hidden">Loading…</span>
              </Spinner>
            </div>
          )}

          {error && !loading && <div className="bd-error" role="alert">{error}</div>}

          {!loading && !error && (
            todayList.length > 0 ? (
              <div className="bd-spot-row">
                {todayList.map((s) => (
                  <SpotlightCard key={keyOf(s)} student={s} onCreatePoster={handlePoster} />
                ))}
              </div>
            ) : (
              <div className="bd-hero__empty">
                <strong>No birthdays today.</strong>{" "}
                {nextUp
                  ? `Next up: ${nextUp.full_name} on ${fmtFull(nextUp.next_birthday)} (${whenLabel(nextUp.days_until).toLowerCase()}).`
                  : "No active students with a date of birth on file."}
              </div>
            )
          )}
        </header>

        {!loading && !error && (
          <>
            {/* ===== Upcoming ===== */}
            <section className="bd-panel">
              <div className="bd-panel__head">
                <div>
                  <h2 className="bd-h2">Coming up</h2>
                  <p className="bd-panel__sub">Next {data.window_days} days · confirm DOB before posting</p>
                </div>
                <div className="bd-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={filter === "all"}
                    className={`bd-tab ${filter === "all" ? "is-on" : ""}`} onClick={() => setFilter("all")}>
                    All <span>{soonList.length}</span>
                  </button>
                  <button type="button" role="tab" aria-selected={filter === "week"}
                    className={`bd-tab ${filter === "week" ? "is-on" : ""}`} onClick={() => setFilter("week")}>
                    This week <span>{weekList.length}</span>
                  </button>
                </div>
              </div>

              <div className="bd-panel__body">
                {shown.length === 0 ? (
                  <p className="bd-empty">No upcoming birthdays in this range.</p>
                ) : (
                  <div className="bd-grid">
                    {shown.map((s) => (
                      <UpcomingCard key={keyOf(s)} student={s} onCreatePoster={handlePoster} />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* ===== New joiners ===== */}
            <section className="bd-panel">
              <div className="bd-panel__head">
                <div>
                  <h2 className="bd-h2">New joiners</h2>
                  <p className="bd-panel__sub">Active students by date of joining</p>
                </div>
                <div className="bd-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={joinTab === "current"}
                    className={`bd-tab ${joinTab === "current" ? "is-on" : ""}`} onClick={() => setJoinTab("current")}>
                    {curMonthLabel} <span>{joinCurrent.length}</span>
                  </button>
                  <button type="button" role="tab" aria-selected={joinTab === "previous"}
                    className={`bd-tab ${joinTab === "previous" ? "is-on" : ""}`} onClick={() => setJoinTab("previous")}>
                    {prevMonthLabel} <span>{joinPrevious.length}</span>
                  </button>
                </div>
              </div>
              <div className="bd-panel__body bd-panel__body--short">
                {joinShown.length === 0 ? (
                  <p className="bd-empty">
                    No students joined in {joinTab === "current" ? curMonthLabel : prevMonthLabel}.
                  </p>
                ) : (
                  <div className="bd-grid">
                    {joinShown.map((s) => (
                      <JoinerCard key={keyOf(s)} student={s} />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* ===== Everyone else ===== */}
            <Accordion
              activeKey={openKey}
              onSelect={(k) => setOpenKey(k === openKey ? null : k)}
              className="bd-accordion"
            >
              <Accordion.Item eventKey="others">
                <Accordion.Header>
                  All other students <span className="bd-count">{others}</span>
                </Accordion.Header>
                <Accordion.Body>
                  {openKey === "others" && (
                    <>
                      <input
                        type="search"
                        className="bd-search"
                        placeholder="Search by name or admission number"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label="Search students"
                      />
                      {filteredOthers.length === 0 ? (
                        <p className="bd-empty">No students match your search.</p>
                      ) : (
                        <div className="bd-table" role="table">
                          <div className="bd-trow bd-trow--head" role="row">
                            <span role="columnheader">Student</span>
                            <span role="columnheader">Date of birth</span>
                            <span role="columnheader">Next birthday</span>
                          </div>
                          <div className="bd-tbody">
                            {filteredOthers.map((s) => (
                              <div className="bd-trow" role="row" key={keyOf(s)}>
                                <span className="bd-tcell-name" role="cell">
                                  <Avatar student={s} size={30} />
                                  <span>
                                    <b title={s.full_name}>{s.full_name}</b>
                                    <small>{s.admission_number ? `ID ${s.admission_number}` : "—"}</small>
                                  </span>
                                </span>
                                <span role="cell">{fmtFull(s.dob)}</span>
                                <span role="cell">
                                  {fmtFull(s.next_birthday)} <small>· {whenLabel(s.days_until)}</small>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </>
        )}
      </div>
    </section>
  );
}